/**
 * ContactReaderPane - reading a contact's document without leaving the page.
 *
 * Opening one used to dispatch `showModal({ type: 'attachmentViewer' })`, a
 * centred modal over the whole page that covered the Documents table you
 * opened it from: you could read the file or see the list, never both. As a
 * pane it docks, resizes, and sits beside the list.
 *
 * The blob is still fetched by `useGlobalStreaming` in the page, so nothing
 * about the download path changed - only where the result is shown.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import ReaderComponent from '../../shared/TailwindComponents/ReaderComponent';
import type { ContactReaderContent } from './ContactSurfaceContext';
import Spinner from '../../shared/TailwindComponents/Spinner';

interface Props {
  content: ContactReaderContent | null;
  onClose: () => void;
}

const ContactReaderPane: React.FC<Props> = ({ content, onClose }) => {
  const { t } = useTranslation();

  if (!content) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <p className="text-xs text-fg-muted text-center">
          {t('Open a document to read it here.')}
        </p>
      </div>
    );
  }

  if (content.loading || !content.citation) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 p-6">
        <Spinner size="sm" />
        <p className="text-xs text-fg-muted text-center truncate max-w-full">{content.title}</p>
      </div>
    );
  }

  return (
    <ReaderComponent
      citation={content.citation}
      matterId={content.clientUuid}
      sourceType="file"
      userAccessLevel={content.userAccessLevel}
      onClose={onClose}
    />
  );
};

export default ContactReaderPane;
